import {DialogTitle} from "@mui/material";
import {Dialog2} from "../../../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../../../misc/SmoothActionBar";
import {useAsyncAction} from "nate-react-api-helpers";
import {api} from "../../../../../../api/API";
import {useSyncedRef} from "../../../../../../misc/SyncedRef";
import {ManualDimension} from "../../../extras/ManualDimension";
import {ProductLookup, ProductLookupKind} from "../../../extras/ProductLookup";
import {Product, ProductScript, ProductSearchResult} from "../../../../../../api/Products";
import React, {useRef} from "react";
import useWindowSize from "react-use/lib/useWindowSize";
import {NumberCol, StringCol, Table, useTableRefresher, ViewOnly} from "../../../../../../misc/scroller/Table";
import {grey} from "@mui/material/colors";
import {DimensionCol} from "../../../extras/DimensionCol";
import {useProjectId} from "../../../ProjectName";
import {removeRowOption, RowOptionCol} from "../../../../../logistics/purchasing/RowOptions";
import {PopperZIndexProvider} from "../../../../../../misc/scroller/PopperZIndexProvider";
import {ChangeDetail, ManualScriptInput, StandaloneHardwareItem} from "../../../../../../api/ProjectFreeformChange";

export function AddHardware(props: {
    change: number;
    detail?: ChangeDetail;
    mode: string;
    kind: "hardware" | "wr-accessory" | "frame-anchor";
    onDone(): void;
    onCancel(): void;
}) {
    const onDoneRef = useSyncedRef(props.onDone);
    const submit = useAsyncAction(async (input: Parameters<typeof api.projectFreeformChange.addSpecProducts>[0]) => {
        await api.projectFreeformChange.addSpecProducts(input)
        onDoneRef.current();
    }, []);

    const win = useWindowSize();
    const list = useRef((props.detail?.standaloneHardware || []));
    const tableName = "project-changes.hardware-table";
    const refresh = useTableRefresher(tableName);
    const idRef = useRef(1);
    const projectId = useProjectId();

    const onSubmit = () => {
        submit.callback({
            id: props.detail?.id,
            change: props.change,
            mode: props.mode as any,
            linkedOpening: undefined,
            project: projectId,
            qty: 1,
            standaloneHardware: list.current.filter(l => !l.archived),
            returnItems: [],
            openingHardware: [],
        })
    };

    return (
        <Dialog2 open onClose={props.onDone} disableEnforceFocus onEnterPress={onSubmit}>
            <DialogTitle>
                {props.kind === "hardware" && <>Send Misc Hardware</>}
                {props.kind === "wr-accessory" && <>Send W/R Accessories</>}
                {props.kind === "frame-anchor" && <>Send Anchors</>}
            </DialogTitle>
            <div style={{
                minHeight: win.height * 0.75,
                display: "flex",
                flexDirection: "column",
                position: "relative"
            }}>
                <PopperZIndexProvider value={1400}>
                    <Table<StandaloneHardwareItem>
                        name={tableName}
                        columns={[
                            RowOptionCol({
                                options: (dt) => [
                                    removeRowOption(dt, {
                                        refreshTables: [tableName],
                                    }),
                                ],
                            }),
                            ViewOnly(StringCol("Name", "category")),
                            ViewOnly(StringCol("Code", "productCode")),
                            NumberCol("Qty", "activeQty"),
                            ViewOnly(StringCol("Mfg", "manufacturerName")),
                            DimensionCol("Length", "dimLength"),
                            DimensionCol("Width", "dimWidth"),
                            DimensionCol("Height", "dimHeight"),
                        ]}
                        fetch={() => Promise.resolve(list.current)}
                        fetchDeps={[]}
                        onChange={async (v) => {
                            list.current = list.current.map(l => {
                                if (l.id === v.id) {
                                    return Object.assign({}, v, {
                                        updatedAt: new Date().toISOString(),
                                    });
                                }

                                return l;
                            });

                            list.current = list.current.filter(v => !v.archived);
                        }}
                    />
                </PopperZIndexProvider>

                <div style={{height: 16}}/>
                <AddItem kind={props.kind === "wr-accessory" ? "div-10" : props.kind === "frame-anchor" ? "frame-anchor" : "hardware"} onAdd={(v, extra) => {
                    list.current.push({
                        id: (idRef.current++),
                        name: v.name,
                        productObj: v,
                        product: v.id,
                        productCode: v.productCode,
                        category: v.categoryName,
                        manufacturer: v.manufacturer,
                        manufacturerName: v.manufacturerName,
                        dimHeight: v.dimHeight,
                        dimWidth: v.dimWidth,
                        dimLength: v.dimLength,
                        activeQty: 1,
                        updatedAt: new Date().toISOString(),
                        archived: false,
                        handing: extra.handing || "",
                        manualScriptInputs: extra.scriptInputs,
                    });

                    refresh();
                }}/>
            </div>
            <SmoothActionBar
                loading={submit.loading}
                error={submit.error}
                actionName={props.detail ? "Update" : "Add"}
                onCancel={() => props.onCancel()}
                onSubmit={onSubmit}
            />
        </Dialog2>
    )
}

export function AddItem(props: {
    kind?: ProductLookupKind;
    manualDimension?: boolean;
    onAdd(item: Product, extra: {
        handing?: string;
        scriptInputs?: ManualScriptInput[];
    }): void;
}) {
    const [showManualDimensions, setShowManualDimensions] = React.useState<{
        product: Product;
        scripts: ProductScript[];
    } | undefined>(undefined);

    return (
        <>
            {showManualDimensions && <ManualDimension<Product>
                value={showManualDimensions.product}
                scripts={showManualDimensions.scripts}
                onCancel={() => setShowManualDimensions(undefined)}
                onDone={async (v, extra) => {
                    props.onAdd(v, extra)
                    setShowManualDimensions(undefined);
                }}
            />}

            <div style={{padding: 2, margin: 8, border: "1px solid " + grey["800"], borderRadius: 4}}>
                <ProductLookup kind={props.kind} onSelect={async (result: ProductSearchResult) => {
                    const productP = api.products.getById(result.id)
                    const scriptsP = api.products.listScripts({ product: result.id });

                    const v = await productP;
                    const scripts = await scriptsP;

                    if(v.dimLengthFormula || v.dimWidthFormula || v.dimHeightFormula ||
                        v.productCode.indexOf("{") !== -1
                        || scripts.data.length > 0
                    ) {
                        if(props.manualDimension === false) {
                            props.onAdd(v, {});
                            return;
                        }

                        setShowManualDimensions({
                            product: v as any,
                            scripts: scripts.data,
                        });
                    } else {
                        props.onAdd(v, {});
                    }
                }} />
            </div>
        </>
    )
}